import {type HarvestDemandPutCertificatesEnum, type HarvestDemandPutSpeciesEnum} from 'client';
import GenericTextField from 'components/Generic/TextField';
import {handleKeyDown} from 'components/InputMenu/helpers';
import {AUTO_FOCUS_CLASS} from 'components/Navigate/Drawers/RightDrawer';
import {CompanySelector} from 'components/Shared/CompanySelector';
import {FishGroupPropertiesTags} from 'components/Shared/FishGroupPropertiesTags';
import {MultiCageSelector} from 'components/Shared/MultiCageSelector';
import {rangeTextHandler} from 'helpers/addEdit/validation';
import NumberFormatCustom from 'helpers/NumberFormatter';
import {isValidDate} from 'helpers/UnitsFormatHandler';
import useAddCageInput from 'hooks/addEdit/useAddEditCageInput';
import {useGetDefaultCompany} from 'hooks/useGetDefaultCompany';
import useParseURL from 'hooks/useParseURL';
import {type InputsState} from 'interfaces';
import {type ChangeEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {StyledAdornedEnd} from 'styles/InputMenu';
import {CertificatesSelector} from './CertificatesSelector';
import InputActionButtons from './InputActionButtons';
import {SmoltSupplySelector} from './Selectors/SmoltSupplySelector';
import {SpeciesSelector} from './SpeciesSelector';
import {MAX_INPUT_WEIGHT, MIN_INPUT_COUNT, MIN_INPUT_WEIGHT} from 'appConstants';
import {InputMenuDatePicker} from 'components/Generic/DatePickersV2/InputMenuDatePicker';
import {Alert, AlertTitle, Button, MenuItem, Stack, TextField} from '@mui/material';

const InputAction = () => {
  const {t} = useTranslation('common');
  const {
    input,
    setInputs,
    inputMenuDispatch,
    fishTypes,
    smoltSupplyDataMismatch,
    onSmoltSupplyChange,
    onSyncSmoltProperties,
    onSubmit,
    isLoading,
  } = useAddCageInput();
  const {planId} = useParseURL();
  const defaultCompany = useGetDefaultCompany();
  const handleChange = (prop: keyof InputsState) => (event: ChangeEvent<HTMLInputElement>) => {
    setInputs({...input, [prop]: parseFloat(event.target.value)});
  };
  const validInputWeight = input.weight === 0 || (input.weight >= MIN_INPUT_WEIGHT && input.weight <= MAX_INPUT_WEIGHT);
  const validInputCount = input.count === 0 || input.count >= MIN_INPUT_COUNT;

  const selectedCageCount = input.treeSelection.cageIds.length;
  const isMultipleCages = selectedCageCount > 1;

  const isSmoltSupplyMismatch = smoltSupplyDataMismatch && Object.values(smoltSupplyDataMismatch).some(Boolean);

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      <Stack spacing={2}>
        {/* Multiple Cage Picker */}
        <MultiCageSelector
          selected={input.treeSelection}
          setSelected={(treeSelection) => {
            setInputs({...input, treeSelection});
          }}
          disabled={!!input.id}
          dataTestId="input-form-site-cage-tree"
        />

        {/* Company */}
        <CompanySelector
          color={smoltSupplyDataMismatch?.company ? 'warning' : 'primary'}
          planId={planId}
          companyId={input.companyId ?? defaultCompany?.id ?? ''}
          setCompanyId={(companyId) => {
            setInputs({...input, companyId});
          }}
        />

        {/* Input Date */}
        <InputMenuDatePicker
          value={input.date}
          onChange={(date: Date | null) => {
            if (!date) {
              return;
            }
            setInputs({...input, date});
          }}
          slotProps={{
            textField: {
              color: smoltSupplyDataMismatch?.date ? 'warning' : 'primary',
            },
          }}
        />

        {/* Smolt Supply */}
        <SmoltSupplySelector
          planId={planId}
          smoltSupplyId={input.smoltSupplyId ?? ''}
          onChange={onSmoltSupplyChange}
          selectedDates={[input.date]}
        />

        {/* Multiple Inputs Warning */}
        {isMultipleCages && (
          <Alert severity="info">
            <AlertTitle>{t('inputCreatingMultiple', {count: selectedCageCount})}</AlertTitle>
            {t('inputCreatingMultipleDescription', {count: selectedCageCount})}
          </Alert>
        )}

        {/* Input Count */}
        <GenericTextField
          inputProps={{'data-testid': 'count'}}
          InputProps={{
            inputComponent: NumberFormatCustom as (props) => JSX.Element,
            endAdornment: <StyledAdornedEnd>{t('fish')}</StyledAdornedEnd>,
            autoFocus: true,
            className: AUTO_FOCUS_CLASS,
          }}
          label={t('count')}
          value={input.count > 0 ? input.count : ''}
          onChange={handleChange('count')}
          onKeyDown={(event) => {
            inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, onSubmit);
          }}
          error={!validInputCount}
          helperText={!validInputCount && t('inputCountErrorHelperText', {count: MIN_INPUT_COUNT})}
        />
        {/* Input Weight */}
        <TextField
          slotProps={{
            htmlInput: {'data-testid': 'weight'},
            input: {
              inputComponent: NumberFormatCustom as (props) => JSX.Element,
              endAdornment: <StyledAdornedEnd>{t('grams')}</StyledAdornedEnd>,
            },
            inputLabel: {shrink: true},
          }}
          color={smoltSupplyDataMismatch?.weight ? 'warning' : 'primary'}
          label={t('averageWeight')}
          value={input.weight > 0 ? input.weight : ''}
          onChange={handleChange('weight')}
          onKeyDown={(event) => {
            inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, onSubmit);
          }}
          error={!validInputWeight}
          helperText={!validInputWeight ? rangeTextHandler(input.weight, MIN_INPUT_WEIGHT, MAX_INPUT_WEIGHT) : null}
        />

        {/* Fish Type Picker */}
        {fishTypes && fishTypes.length > 0 && (
          <TextField
            select
            color={smoltSupplyDataMismatch?.fishType ? 'warning' : 'primary'}
            value={input?.fishTypeId}
            onChange={(event) => {
              const fishTypeId = event.target.value;
              setInputs({...input, fishTypeId});
            }}
            label={t('fishType')}
            slotProps={{
              htmlInput: {'data-testid': 'fishType'},
            }}
            aria-label="fishType"
          >
            {fishTypes.map((fishType) => (
              <MenuItem key={fishType.id} value={fishType.id}>
                {fishType.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Fish Species */}
        <SpeciesSelector
          color={smoltSupplyDataMismatch?.species ? 'warning' : 'primary'}
          species={input.species}
          setSpecies={(species?: HarvestDemandPutSpeciesEnum) => {
            species && setInputs({...input, species});
          }}
        />
        {/* Fish Certificates */}
        <CertificatesSelector
          color={smoltSupplyDataMismatch?.certificates ? 'warning' : 'primary'}
          certificates={input.certificates}
          setCertificates={(certificates: HarvestDemandPutCertificatesEnum[]) => {
            setInputs({...input, certificates});
          }}
        />
        {/* Fish Group properties tags */}
        <FishGroupPropertiesTags
          {...(smoltSupplyDataMismatch?.fishGroupProperties && {color: 'warning'})}
          selectedTags={input.fishGroupPropertyIds}
          onAfterCreate={(newProperty) => {
            setInputs({...input, fishGroupPropertyIds: [...input.fishGroupPropertyIds, newProperty]});
          }}
          onSelectionChange={(selectedFishGroupProperties) => {
            setInputs({...input, fishGroupPropertyIds: selectedFishGroupProperties});
          }}
          label="Properties"
          helperText={
            smoltSupplyDataMismatch?.fishGroupProperties
              ? t('inputGroupPropertiesSmoltMismatch')
              : t('fishGroupPropertiesHelperText')
          }
        />
        {isSmoltSupplyMismatch && (
          <Alert severity="warning" icon={false}>
            <AlertTitle>{t('inputSmoltMismatchAlertDescription')}</AlertTitle>
            <Button onClick={onSyncSmoltProperties} variant="outlined" color="warning" size="small" sx={{mt: 1}}>
              {t('syncProperties')}
            </Button>
          </Alert>
        )}
        <InputActionButtons
          disabled={
            input.weight === 0 || input.count === 0 || !isValidDate(input.date) || !validInputWeight || !validInputCount
          }
          loading={isLoading}
          onSubmit={onSubmit}
        />
      </Stack>
    </form>
  );
};

export default InputAction;
